import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faBars,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import constants from "../constants/constants";
import { useUserParams } from "../hooks/useUserParams";

export default function MobileHeader({ location, path, isDark }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const { getCurrentLogo } = useUserParams();

  const toggleMenu = (slug) => {
    setActiveMenu((prev) => (prev === slug ? null : slug));
  };

  const drawerVariants = {
    closed: {
      x: "-100%",
      transition: {
        duration: 0.45,
      },
    },
    open: {
      x: 0,
      transition: {
        delay: 0.15,
        duration: 0.45,
        ease: [0.30, 1, 0.36, 1],
        when: "beforeChildren",
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      x: -20,
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.35,
      },
    },
  };

  return (
    <header
      className="absolute top-0 left-0 right-0 z-[100]"
    >
      <nav
        className={`flex h-20 items-center justify-between px-5`}
      >
        {/* Logo */}
        <Link to="/">
          <img
            src={getCurrentLogo()}
            className="h-8"
            alt="Logo"
          />
        </Link>

        <motion.button
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => setIsOpen(true)}
          className="flex h-12 w-12 items-center justify-center rounded-lg bg-black/80 border border-white/10 transition-all duration-300 hover:bg-black"
        >
          <FontAwesomeIcon
            icon={faBars}
            className="text-xl text-white/90"
          />
        </motion.button>
      </nav>


      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.nav
              variants={drawerVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 left-0 z-50 flex h-screen w-[85%] max-w-sm flex-col bg-[#181818] px-8 pt-8 shadow-2xl border-r border-white/10"
            >
              {/* Close */}
              <div className="mb-12 flex items-center justify-between">
                <img
                  src={getCurrentLogo()}
                  alt="Logo"
                  className="h-12"
                />

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ rotate: 90 }}
                  onClick={() => setIsOpen(false)}
                  className="h-12 w-12 rounded-lg bg-white/10 border border-white/10 transition-all duration-300 hover:bg-white/20"
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="text-2xl text-white/90"
                  />
                </motion.button>
              </div>

              {/* Links */}
              <div className="flex flex-col">
                {
                  constants.NAVIGATION.map((item) => {
                    if (!item.children) {
                      return (
                        <motion.div key={item.title} variants={itemVariants}>
                          <Link
                            to={item.to}
                            onClick={() => setIsOpen(false)}
                            className="block border-b border-white/10 py-5 uppercase tracking-[0.2em] text-sm text-white/80 transition-all duration-300 hover:text-white hover:pl-2"
                          >
                            {item.title}
                          </Link>
                        </motion.div>
                      );
                    }

                    return (
                      <motion.div
                        key={item.title}
                        variants={itemVariants}
                        className="border-b border-white/10"
                      >
                        {/* Parent Menu */}
                        <button
                          onClick={() => toggleMenu(item.slug)}
                          className="flex w-full items-center justify-between py-5 uppercase tracking-[0.2em] text-sm text-white/80 transition-all duration-300 hover:text-white"
                        >
                          <span>{item.title}</span>

                          <FontAwesomeIcon
                            icon={faAngleDown}
                            className={`duration-300 text-white/50 text-lg ${activeMenu === item.slug ? "rotate-180 text-white" : ""}`}
                          />
                        </button>

                        {/* Submenus */}
                        <AnimatePresence>
                          {activeMenu === item.slug && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden pb-3"
                            >
                              {item.children.map((child) => (
                                <Link
                                  key={child.to}
                                  to={child.to}
                                  onClick={() => setIsOpen(false)}
                                  className="block py-3 pl-6 text-sm uppercase tracking-[0.15em] text-white/60 transition-all duration-300 hover:text-white hover:pl-8"
                                >
                                  {child.title}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                <motion.div variants={itemVariants}>
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="block border-b border-white/10 py-5 uppercase tracking-[0.2em] text-sm text-white/80 transition-all duration-300 hover:text-white hover:pl-2"
                  >
                    Contact
                  </Link>
                </motion.div>
              </div>
            </motion.nav >

          </>
        )}
      </AnimatePresence >
    </header>
  );
}